import React, { useState } from 'react';
import _debounce from 'lodash/debounce';

import {
  isDesktop as getIsDesktop,
  isLargeDesktop as getIsLargeDesktop,
  isMobile as getIsMobile,
  isTablet as getIsTablet,
  isXsMobile as getIsXsMobile,
} from '@zola-helpers/client/dist/es/util/responsive';

import { UAParser } from 'ua-parser-js';

import useEffectOnce from '../../hooks/useEffectOnce';

import {
  DeviceTypeState,
  ResponsiveDesignContext,
  ResponsiveDesignState,
} from './ResponsiveDesignContext';

/**
 * From the user agent, determine the device type as best as we can determine.
 *
 * Note that this does not know anything about the size of the device, but if
 * the user agent is iphone or android, we can assume it's a mobile device and
 * use that information to render a page for mobile devices on initial load.
 */
export const getDeviceTypeState = (userAgent?: string): DeviceTypeState => {
  const deviceType = new UAParser(userAgent).getDevice().type || 'desktop';
  const isMobileDevice = deviceType === 'mobile';
  const isTabletDevice = deviceType === 'tablet';
  const isDesktopDevice = !isTabletDevice && !isMobileDevice;

  return {
    isDesktopDevice,
    isTabletDevice,
    isMobileDevice,
  } as DeviceTypeState;
};

const getResponsiveDesign = (userAgent?: string): ResponsiveDesignState => {
  const deviceTypeState = getDeviceTypeState(userAgent);

  const { isDesktopDevice, isTabletDevice, isMobileDevice } = deviceTypeState;

  let isLargeDesktop = false;
  let isDesktop = isDesktopDevice;
  let isTablet = isTabletDevice;
  let isMobile = isMobileDevice;
  let isXsMobile = false;

  if (typeof window !== 'undefined') {
    isLargeDesktop = getIsLargeDesktop();
    isDesktop = getIsDesktop();
    isTablet = getIsTablet();
    isMobile = getIsMobile();
    isXsMobile = getIsXsMobile();
  }

  return {
    ...deviceTypeState,
    isLargeDesktop,
    isDesktop,
    isTablet,
    isMobile,
    isXsMobile,
  };
};

export const ResponsiveDesignProvider = ({
  userAgent,
  children,
}: {
  userAgent?: string;
  children: React.ReactNode;
}): JSX.Element => {
  const [responsiveDesign, setResponsiveDesign] = useState(getResponsiveDesign(userAgent));

  const handleWindowResize = _debounce(() => {
    setResponsiveDesign(getResponsiveDesign(userAgent));
  }, 500);

  useEffectOnce(() => {
    setResponsiveDesign(getResponsiveDesign(userAgent));
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  });

  return (
    <ResponsiveDesignContext.Provider value={{ ...responsiveDesign }}>
      {children}
    </ResponsiveDesignContext.Provider>
  );
};
